import {
  graphQLErrorCode,
  useChangeWorkspace,
  useCurrentAccount,
  useRedeemAssetShareLink,
  useResolveAssetShareLink,
  useStorageKeys,
} from '@gripp/shared-logic';
import { Colors, LoadingIndicator, tokens } from '@gripp/shared-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';
import { buildOneLinkUrl, isMobileBrowser } from '../../anonymous/oneLinkUtils';
import { usePathWithParams } from '../../shared/hooks/usePathWithParams';

/**
 * The create-profile page a signed-out desktop recipient is sent to, carrying
 * the share token where the invitation flow carries an invitation id. The
 * static `share` segment outranks `/acceptInvitation/:invitationId/:workspaceId`
 * on React Router's score, so the two coexist without ordering games.
 */
export const acceptSharePath = (token: string): string =>
  `/acceptInvitation/share/${token}`;

/** The recipient's own asset route, resolved from their ACTIVE workspace. */
const memberAssetPath = (assetId: string): string => `/assets/${assetId}`;

/** The admin route, which names the workspace instead of assuming it. */
const prefixedAssetPath = (workspaceId: string, assetId: string): string =>
  `/workspaces/${workspaceId}/asset/${assetId}`;

/** The envelope `changeWorkspace` resolves to. */
type ChangeWorkspaceResult = {
  account?: { changeWorkspace?: { success?: boolean | null } | null } | null;
};

export type SharedAssetLandingArgs = {
  workspaceId: string;
  assetId: string;
  accountId?: string;
  /** The workspace the recipient is in right now, before any switch. */
  activeWorkspaceId?: string;
  changeWorkspace: (variables: {
    input: { id: string; workspaceId: string };
  }) => Promise<unknown>;
};

/**
 * Moves a redeemed recipient into the asset's workspace and reports where to
 * land them. One function rather than a path builder because the destination
 * IS the outcome of the switch, and the two web landing sites must not drift.
 *
 * A member lands on `/assets/:assetId`, which resolves the asset from their
 * active workspace. The workspace-prefixed route is the fallback, not the
 * default, because every asset sub-route under that prefix requires `*.*` —
 * so a non-admin sent there reaches the landing page and nothing else: chat,
 * edit, activity, routines and files are all `/forbidden`.
 *
 * The switch is what tells the two apart. `changeWorkspace` refuses a caller
 * with no membership record — which is exactly the `*.*` holder whose
 * redemption wrote nothing — reporting it as `success: false`, or as a throw
 * for a caller holding the workspace in `allowedWorkspaces` without a role.
 * Both mean "not a member here", so both take the prefix, where `*.*` passes
 * every gate.
 */
export const sharedAssetLanding = async ({
  workspaceId,
  assetId,
  accountId,
  activeWorkspaceId,
  changeWorkspace,
}: SharedAssetLandingArgs): Promise<string> => {
  // No account to switch, so no membership was ever established.
  if (!accountId) return prefixedAssetPath(workspaceId, assetId);

  // Already there — a Guest upgraded in place, or a `*.*` holder whose active
  // workspace is the target. Moving them would be a change nothing asked for,
  // and their active workspace already resolves the asset.
  if (activeWorkspaceId === workspaceId) return memberAssetPath(assetId);

  try {
    const result = (await changeWorkspace({
      input: { id: accountId, workspaceId },
    })) as ChangeWorkspaceResult;

    return result?.account?.changeWorkspace?.success === true
      ? memberAssetPath(assetId)
      : prefixedAssetPath(workspaceId, assetId);
  } catch {
    return prefixedAssetPath(workspaceId, assetId);
  }
};

/**
 * The param name the share token rides under in `deep_link_value`, the way
 * `invitationId` already does. `registerForm` reads it back off the deferred
 * deep link after the install.
 */
const ONELINK_TOKEN_PARAM = 'shareToken';

export type JoinSharedAssetViewProps = {
  token: string;
  /**
   * The viewer is signed out. There is no anonymous asset surface behind an
   * asset link — deliberately unlike the activity share — so nothing of the
   * asset is rendered before an account exists.
   */
  signedOut?: boolean;
};

type ResolvedShareTarget = {
  assetId?: string | null;
  workspaceId?: string | null;
};

type RedeemedShareLink = {
  joined?: boolean;
  assetId?: string | null;
  workspaceId?: string | null;
};

/** Which refusal the recipient is looking at. */
type Refusal = 'removed' | 'generic';

/**
 * `/share/:token` when the link names an ASSET: the invitation flow's
 * three-way dispatch, mirroring `anonymous/invitation.tsx`.
 *
 * 1. Mobile browser → the AppsFlyer OneLink, token on `deep_link_value`.
 *    Decided before auth, as `invitation.tsx` decides it: on a phone the
 *    destination is the app, and the token is redeemed after the install
 *    (`registerForm`) or by the native `share/[token]` route if the app is
 *    already there and claimed the URL before the browser ever saw it.
 * 2. Desktop, signed out → the create-profile page, token in the path.
 * 3. Desktop, signed in → redemption runs on open and the recipient lands on
 *    the asset. No screen, no button.
 *
 * Redemption in an effect is deliberate: the link *is* the grant, so opening
 * it is the act of joining, exactly as opening a workspace invitation is
 * (revised 2026-09-10; this replaces an earlier "never in an effect" rule,
 * whose reasoning was that a screen stood between the link and the write).
 * A ref guard is what keeps it to one call, as in `acceptInvitation.tsx`.
 *
 * What refuses is therefore all this still renders: a member the workspace
 * removed, a subject deleted out from under the link, and a token that stops
 * resolving between the resolve and the redeem. A token that is already
 * revoked, expired or unknown never reaches here — `ShareView`'s own resolve
 * fails first and sends every subject type home, asset links included.
 */
export const JoinSharedAssetView = ({
  token,
  signedOut = false,
}: JoinSharedAssetViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storageKeys = useStorageKeys();
  const currentPath = usePathWithParams();
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { mutateAsync: redeem } = useRedeemAssetShareLink();
  const { mutateAsync: changeWorkspace } = useChangeWorkspace();
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // A refusal belongs to the token that earned it.
  const refusalTokenRef = useRef(token);
  if (refusalTokenRef.current !== token) {
    refusalTokenRef.current = token;
    if (refusal) setRefusal(null);
  }

  const isMobile = isMobileBrowser();
  // Both of those branches leave this page without reading anything off the
  // link, so the request would be waste. `ShareView` has already resolved the
  // token once — that is how this component was chosen — and it is that
  // resolve, not this one, that decides an ASSET link goes to the app rather
  // than an ISSUE link's mobile-web surface.
  const leavesImmediately = isMobile || signedOut;
  const { data, isLoading, isError } = useResolveAssetShareLink(
    leavesImmediately ? undefined : token
  ) as {
    data?: { share?: { resolveShareLink?: ResolvedShareTarget } };
    isLoading: boolean;
    isError: boolean;
  };
  const target = data?.share?.resolveShareLink;

  // Keyed on the token, not a bare flag: react-router reuses this component
  // when only the param changes, so a second link opened in the same session
  // would find a one-way latch already set and dispatch nothing.
  const dispatchedRef = useRef<string>();
  useEffect(() => {
    if (dispatchedRef.current === token) return;

    if (isMobile) {
      dispatchedRef.current = token;
      window.location.replace(
        buildOneLinkUrl({ [ONELINK_TOKEN_PARAM]: token })
      );
      return;
    }

    if (signedOut) {
      dispatchedRef.current = token;
      // The return trip, for the recipient who turns out to have an account
      // already: the create-profile page sends them through
      // /verify-auth-code, which reads `redirectPath` and lands them back
      // here signed in, where the branch below redeems. `pendingDestination`
      // is the durable copy `ProtectedRoute` reads, because a brand-new
      // account is routed through /onboarding, which never reads the first.
      localStorage.setItem(storageKeys.redirectPath, currentPath);
      localStorage.setItem(storageKeys.pendingDestination, currentPath);
      navigate(acceptSharePath(token), { replace: true });
      return;
    }

    if (isLoading || isAccountLoading || isError || !target) return;
    // A deleted subject nulls both ids; redeeming would be refused and the
    // render below names it instead.
    if (!target.assetId || !target.workspaceId) return;

    // Set before the call, not after it: a refusal must not be retried by the
    // re-render that shows it.
    dispatchedRef.current = token;

    const redeemAndLand = async () => {
      try {
        const result = (await redeem({ token })) as {
          share?: { redeemShareLink?: RedeemedShareLink };
        };
        const redeemed = result?.share?.redeemShareLink;
        const assetId = redeemed?.assetId;
        const workspaceId = redeemed?.workspaceId;
        if (!assetId || !workspaceId) {
          setRefusal('generic');
          return;
        }

        // `redeemShareLink` writes membership and nothing else: a Restricted
        // member reaches their asset only while switched into its workspace,
        // so the switch is the client's job, and its outcome is what picks
        // the destination. `joined: false` is not "nothing happened" (an
        // existing Restricted member comes back false with the asset newly
        // assigned), so it never decides whether to navigate.
        const destination = await sharedAssetLanding({
          workspaceId,
          assetId,
          accountId: account?.id,
          activeWorkspaceId: account?.workspace?.id,
          changeWorkspace,
        });

        // A hard navigation, as the workspace switcher does. Every cached read
        // on this page belongs to the workspace the recipient was in a moment
        // ago, and their own permissions have just changed — the route gates
        // read them off the Account object built at load.
        window.location.assign(destination);
      } catch (err) {
        // `FORBIDDEN` is redeemShareLink's code for a caller on this
        // workspace's removedWorkspaces; every other refusal it raises is
        // `NOT_FOUND`. Read the code, never the message.
        setRefusal(
          graphQLErrorCode(err) === 'FORBIDDEN' ? 'removed' : 'generic'
        );
      }
    };

    redeemAndLand().catch(() => setRefusal('generic'));
  }, [
    account?.id,
    account?.workspace?.id,
    changeWorkspace,
    currentPath,
    isAccountLoading,
    isError,
    isLoading,
    isMobile,
    navigate,
    redeem,
    signedOut,
    storageKeys.pendingDestination,
    storageKeys.redirectPath,
    target,
    token,
  ]);

  // Nothing to show on the way out.
  if (leavesImmediately) {
    return <LoadingIndicator />;
  }

  if (refusal) {
    return (
      <ShareRefusalMessage testID="join-shared-asset-error">
        {t(
          refusal === 'removed' ? 'chat.join.removedError' : 'chat.join.error'
        )}
      </ShareRefusalMessage>
    );
  }

  if (isLoading || isAccountLoading) {
    return <LoadingIndicator />;
  }

  if (isError || !target) {
    return (
      <ShareRefusalMessage testID="join-shared-asset-resolve-error">
        {t('chat.join.resolveError')}
      </ShareRefusalMessage>
    );
  }

  // Both ids go null together once the workspaceasset, the asset or the
  // workspace is soft-deleted. The link still resolves — naming it is this
  // screen's call, not the field's — and redeeming would be refused.
  if (!target.assetId || !target.workspaceId) {
    return (
      <ShareRefusalMessage testID="join-shared-asset-unavailable">
        {t('chat.join.unavailable')}
      </ShareRefusalMessage>
    );
  }

  // Redemption is in flight; the happy path replaces this page.
  return <LoadingIndicator />;
};

/**
 * The refusal card for the share flow, shared with the create-profile page so
 * a refusal reads the same wherever in the flow it lands.
 */
export const ShareRefusalMessage = ({
  children,
  testID,
}: {
  children: string;
  testID: string;
}) => (
  <View style={styles.container} testID={testID}>
    <View style={styles.card}>
      <Text style={styles.message}>{children}</Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: 1,
    borderColor: Colors.role.borderSection,
    backgroundColor: Colors.role.surface,
  },
  message: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
});
